import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import parse from 'html-react-parser';
import { useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { formatBytes } from '@/components/core/Uploader/utils';
import useDataModelResource from '@/hooks/useDataModelResource';
import useOrganisationService from '@/hooks/useOrganisationService';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

interface DataModelInfo {
    activateable: boolean;
    displayName: string;
    ownerName: string;
    version: string;
    updatedAt: string;
    dataModelDeployedAt: string;
    translatedDescription: string;
    translatedReleaseNotes: string;
    imageUrl: string;
    active: boolean;
    ownerEmail: string;
    url: string;
    id: string;
    isSystemDataModel: boolean;
    gemRepositorySize?: number;
}

interface DataModelExchangeDialogProps {
    dataModelInfo: DataModelInfo;
    hasActivatePermisson: boolean;
    onClose: (toWait?: boolean) => void;
}

const DataModelExchangeDialog = ({
    dataModelInfo,
    hasActivatePermisson,
    onClose,
}: DataModelExchangeDialogProps) => {
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const { toast } = useToast();

    const { dataModelResource } = useDataModelResource();
    const { organisationService } = useOrganisationService();

    const modalInfo = [
        {
            key: t('DATA_MODEL.OWNER_NAME'),
            value: dataModelInfo.ownerName,
        },
        {
            key: t('VERSION'),
            value: dataModelInfo.version,
        },
        {
            key: t('UPDATED'),
            value: dataModelInfo.updatedAt
                ? `${new Date(dataModelInfo.updatedAt).toLocaleDateString()}
                ${new Date(dataModelInfo.updatedAt).toLocaleTimeString()}
                `
                : null,
        },
        {
            key: t('DATA_MODEL.OWNER_EMAIL'),
            value: dataModelInfo.ownerEmail,
        },
        {
            key: t('DEPLOYED'),
            value:
                dataModelInfo.dataModelDeployedAt && dataModelInfo.ownerName
                    ? `${new Date(
                        dataModelInfo.dataModelDeployedAt,
                    ).toLocaleDateString()}${new Date(
                        dataModelInfo.dataModelDeployedAt,
                    ).toLocaleTimeString()}
                (${t('BY_USER')} ${dataModelInfo.ownerName})`
                    : null,
        },
        {
            key: t('DATA_MODEL.SIZE'),
            value:
                typeof dataModelInfo.gemRepositorySize === 'number' && dataModelInfo.gemRepositorySize > 0
                    ? formatBytes(dataModelInfo.gemRepositorySize)
                    : null,
        },
    ];
    const onsubmit = () => {
        dataModelResource.save(
            {
                modelId: dataModelInfo.id,
                isSystemDataModel: dataModelInfo.isSystemDataModel,
            },
            function () {
                toast.success('MODELEXCHANGE.USE_MODEL_SUCCESSFUL');
                organisationService.update({ isDataModelDeploying: true });
                setOpen(false);
                onClose(true);
            },
            function () {
                toast.error('MODELEXCHANGE.USE_MODEL_ERROR');
                organisationService.update({ isDataModelDeploying: false });
            },
        );
    };
    const handleDownloadClick = (url: string) => {
        window.location.href = url;
    };
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                onPointerDownOutside={() => onClose()}
                onInteractOutside={() => onClose()}
            >
                <DialogHeader>
                    <DialogHeaderDefault>{t('DATA_MODEL.INFO')}</DialogHeaderDefault>
                </DialogHeader>

                <DialogBody className="tw-w-full modal-dialog tw-h-[550px]">
                    <div className="tw-grid tw-grid-cols-6">
                        <div className="tw-col-span-2 tw-flex tw-flex-col tw-items-center">
                            <div className="tw-p-5 tw-border tw-border-solid tw-border-gray-200 tw-flex tw-flex-col tw-items-center">
                                { dataModelInfo.imageUrl &&
                                    <img
                                        alt="model-image"
                                        className="tw-h-[250px] tw-w-[250px] tw-object-contain"
                                        src={dataModelInfo.imageUrl}
                                    />
                                }

                                <div className="tw-mb-4 tw-mt-5 tw-flex tw-flex-col tw-items-center">
                                    <div className="tw-font-bold tw-mb-5 tw-text-center">
                                        {dataModelInfo.displayName}
                                    </div>
                                    { dataModelInfo.url &&
                                        <Button
                                            variant="outline"
                                            className="tw-mx-1"
                                            onClick={() => {
                                                handleDownloadClick(
                                                    dataModelInfo.url,
                                                );
                                            }}
                                        >
                                            <span className="tw-flex tw-justify-center tw-items-center">
                                                <Icon
                                                    icon={ArrowDownTrayIcon}
                                                    size="lg"
                                                    variant="secondary"
                                                />
                                                <span className="tw-ml-2">
                                                    {t('MODELEXCHANGE.DOWNLOAD')}
                                                </span>
                                            </span>
                                        </Button>
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="tw-col-span-4">
                            <div className="tw-scrollbar tw-h-[500px] tw-overflow-y-auto">
                                {modalInfo.map((infoItem, index) => {
                                    if (infoItem.value)
                                        return (
                                            <div
                                                className="tw-grid tw-grid-cols-4 tw-gap-4 tw-my-2"
                                                key={index}
                                            >
                                                <div className="tw-col-span-2 tw-text-base tw-font-bold">
                                                    {infoItem.key}
                                                </div>
                                                <div className="tw-col-span-2 tw-text-base tw-justify-center">
                                                    {infoItem.value}
                                                </div>
                                            </div>
                                        );
                                })}
                                {dataModelInfo.translatedDescription && (
                                    <div className="tw-my-2">
                                        <div className="tw-my-2 tw-text-base tw-font-bold">
                                            {t('DATA_MODEL.DESCRIPTION')}
                                        </div>
                                        <p className="tw-text-gray-800">
                                            {parse(dataModelInfo.translatedDescription)}
                                        </p>
                                    </div>
                                )}
                                {dataModelInfo.translatedReleaseNotes && (
                                    <div className="tw-my-2">
                                        <div className="tw-my-2 tw-text-base tw-font-bold">
                                            {t('DATA_MODEL.RELEASE_NOTES')}
                                        </div>

                                        <p className="tw-text-gray-800">
                                            {parse(dataModelInfo.translatedReleaseNotes)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button
                        id="use-datamodel"
                        type="submit"
                        variant="default"
                        onClick={onsubmit}
                        className={dataModelInfo.id}
                        disabled={dataModelInfo.activateable === false}
                    >
                        {!hasActivatePermisson || dataModelInfo.active
                            ? t('MODELEXCHANGE.REDEPLOY_THIS_DATAMODEL')
                            : t('MODELEXCHANGE.USE_THIS_DATAMODEL')}
                    </Button>
                    <DialogClose asChild={true}>
                        <Button
                            variant="secondary"
                            onClick={() => onClose()}
                        >
                            {t('CLOSE')}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
DataModelExchangeDialog.displayName = 'DataModelExchangeDialog';
export { DataModelExchangeDialog, type DataModelExchangeDialogProps };
